import { useState } from "react";
import { Link, Outlet, createFileRoute } from "@tanstack/react-router";
import { Menu, Package } from "lucide-react";

import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { adminNavItems } from "@/components/admin/nav";
import { RequireSession } from "@/components/RequireSession";
import { useOrdersSummary } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { SiteFooter } from "@/components/SiteCredit";

/**
 * Moldura do painel: sidebar fixa + area de conteudo rolavel.
 *
 * E uma rota pathless (`_painel`) porque o login mora em /admin/entrar e NAO
 * deve herdar a sidebar — quem ainda nao entrou nao tem menu.
 */
export const Route = createFileRoute("/admin/_painel")({
  head: () => ({
    // O painel e interno: nada aqui deve entrar em indice de busca.
    meta: [{ name: "robots", content: "noindex, nofollow" }],
  }),
  component: PainelLayout,
});

const ROLE_LABEL: Record<string, string> = {
  ADMIN: "Administrador",
  ATENDENTE: "Atendente",
};

function PainelLayout() {
  return (
    <RequireSession roles={["ADMIN", "ATENDENTE"]} redirectTo="/admin/entrar">
      <Painel />
    </RequireSession>
  );
}

function Painel() {
  const { profile, logout } = useSession();
  const summary = useOrdersSummary();
  const [menuOpen, setMenuOpen] = useState(false);
  const newOrders = summary.data?.newOrders ?? 0;

  return (
    <div className="flex min-h-screen bg-mb-surface">
      <AdminSidebar
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        items={adminNavItems({
          // O número no menu é o que pede ação: enviados que ninguém abriu.
          pendingOrders: summary.data?.newOrders,
          // Catalogo e equipe mexem em preco e acesso: so administracao.
          isAdmin: profile?.role === "ADMIN",
        })}
        user={{
          name: profile?.name ?? "",
          role: ROLE_LABEL[profile?.role ?? ""] ?? "Equipe",
        }}
        onLogout={() => void logout()}
      />
      <div className="flex min-w-0 grow flex-col">
        {/* Celular e tablet: a barra lateral vira gaveta, aberta por aqui. */}
        <div className="sticky top-0 z-30 flex h-14 items-center gap-3 bg-mb-navy px-4 lg:hidden">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Abrir menu"
            className="relative grid h-9 w-9 place-items-center rounded-full text-white hover:bg-white/10"
          >
            <Menu className="h-5 w-5" />
            {newOrders > 0 && (
              <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-mb-yellow px-1 text-[10px] font-bold text-mb-ink">
                {newOrders}
              </span>
            )}
          </button>
          <Link to="/admin/pedidos" className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-mb-yellow">
              <Package className="h-4 w-4 text-mb-ink" />
            </span>
            <span className="font-display text-base font-bold text-white">MonteBox</span>
          </Link>
        </div>
        <Outlet />
        <SiteFooter className="mt-auto" />
      </div>
    </div>
  );
}
